import React, { useState, useEffect, useCallback } from 'react';
import {
  FaBan,
  FaCheck,
  FaCheckCircle,
  FaPhone,
  FaTachometerAlt,
  FaChevronDown,
  FaChevronUp,
  FaExclamationTriangle,
  FaPlay,
  FaSyncAlt,
  FaTimes
} from 'react-icons/fa';
import LoadingState from '../components/LoadingState';
import { Link } from 'react-router-dom';
import {
  getTranscribeQuota,
  getTranscribeQuotaDay,
  getTranscribeQuotaJob,
  resumeTranscribeQuota,
  denyTranscribeQuota,
  setDefaultQuotaLimit
} from '../api/api';

const BADGE = {
  pca:     { label: 'PCA',     bg: 'bg-cloudza-100 text-cloudza-800' },
  bulk:    { label: 'Bulk',    bg: 'bg-purple-100 text-purple-800' },
  genesys: { label: 'Genesys', bg: 'bg-orange-100 text-orange-800' }
};

function WorkflowBadges({ types = [] }) {
  return (
    <div className="flex gap-1 flex-wrap">
      {[...types].sort().map(t => {
        const b = BADGE[t] || { label: t, bg: 'bg-gray-100 text-gray-800' };
        return (
          <span key={t} className={`px-2 py-0.5 rounded-lg text-xs font-medium ${b.bg}`}>
            {b.label}
          </span>
        );
      })}
    </div>
  );
}

function fmt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

// Show exact count for PCA (1 per call), approximate for Genesys (chunk × 100 estimate)
function CallCount({ job }) {
  const count = job.callCount ?? job.stoppedCount ?? 0;
  const isGenesys = (job.workflowTypes || []).includes('genesys') &&
                    !(job.workflowTypes || []).includes('pca');
  return (
    <span className="font-semibold text-gray-700">
      {isGenesys ? `~${count}` : count}
    </span>
  );
}

export default function TranscribeQuota({ setAlert }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [expandedJob, setExpandedJob] = useState(null);
  const [expandedExecs, setExpandedExecs] = useState([]);
  const [expandLoading, setExpandLoading] = useState(false);
  const [newLimit, setNewLimit] = useState('');
  const [defaultLimit, setDefaultLimit] = useState('');
  const [defaultLimitLoading, setDefaultLimitLoading] = useState(false);
  const [defaultLimitMsg, setDefaultLimitMsg] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [confirmText, setConfirmText] = useState('');

  // Per-day pagination: { "2026-05-24": "<base64nextKey>", ... }
  const [dayNextKeys, setDayNextKeys] = useState({});
  const [dayLoadingMore, setDayLoadingMore] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTranscribeQuota();
      setData(res);
      // Functional form: `newLimit` here is a stale closure ([] deps), which
      // clobbered any typed override on every reload.
      setNewLimit(prev => prev === '' ? String(res.days?.[0]?.limit || 500) : prev);
      // Seed nextKey map from initial response
      const keys = {};
      (res.days || []).forEach(d => { if (d.nextKey) keys[d.date] = d.nextKey; });
      setDayNextKeys(keys);
    } catch (e) {
      setAlert && setAlert({ variant: 'error', heading: 'Error', text: 'Failed to load call limiter data.' });
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Load the next page of jobs for a specific date
  const loadMoreForDay = async (date) => {
    const startKey = dayNextKeys[date];
    if (!startKey) return;
    setDayLoadingMore(prev => ({ ...prev, [date]: true }));
    try {
      const res = await getTranscribeQuotaDay(date, startKey);
      setData(prev => ({
        ...prev,
        days: prev.days.map(d =>
          d.date === date
            ? { ...d, jobs: [...d.jobs, ...(res.jobs || [])], stoppedJobCount: d.stoppedJobCount + (res.jobs || []).length }
            : d
        ),
      }));
      setDayNextKeys(prev => {
        const next = { ...prev };
        if (res.nextKey) next[date] = res.nextKey;
        else delete next[date];
        return next;
      });
    } catch {
      setAlert && setAlert({ variant: 'error', heading: 'Error', text: `Failed to load more paused jobs for ${date}.` });
    } finally {
      setDayLoadingMore(prev => ({ ...prev, [date]: false }));
    }
  };

  // Composite key so same jobId on different days is distinct
  const jobKey = (date, jobId) => `${date}|${jobId}`;
  const parseJobKey = (key) => ({ date: key.slice(0, 10), jobId: key.slice(11) });

  // allJobs keeps date attached for capacity lookups; allJobKeys drives checkbox state
  const allJobs = data?.days?.flatMap(d => d.jobs.map(j => ({ ...j, date: d.date }))) || [];
  const allJobKeys = data?.days?.flatMap(d => d.jobs.map(j => jobKey(d.date, j.jobId))) || [];
  const today = data?.days?.[0] || {};

  const toggleSelect = (date, jobId) => {
    const key = jobKey(date, jobId);
    setSelected(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === allJobKeys.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allJobKeys));
    }
  };

  const expandJob = async (date, jobId) => {
    const key = jobKey(date, jobId);
    if (expandedJob === key) { setExpandedJob(null); return; }
    setExpandedJob(key);
    setExpandLoading(true);
    try {
      const res = await getTranscribeQuotaJob(jobId, date);
      setExpandedExecs(res.executions || []);
    } catch {
      setExpandedExecs([]);
    } finally {
      setExpandLoading(false);
    }
  };

  // Execute the staged action after "confirm" is typed
  const doExecute = async (action) => {
    setPendingAction(null);
    setConfirmText('');
    setActionLoading(true);
    setActionMsg(null);
    try {
      if (action.type === 'deny') {
        const res = await denyTranscribeQuota({ jobs: action.jobs });
        setActionMsg({ type: 'success', text: `Denied ${res.deniedJobs} job(s).` });
      } else {
        // 'resume', 'set-zero', or 'set-limit'
        const limitOverride = action.newLimit !== undefined
          ? action.newLimit
          : (newLimit ? parseInt(newLimit, 10) : undefined);
        const payload = { jobs: action.jobs };
        if (limitOverride !== undefined) payload.newLimit = limitOverride;
        const res = await resumeTranscribeQuota(payload);
        if (action.type === 'set-zero') {
          setActionMsg({ type: 'success', text: `Today's call limit set to 0. All new calls will be paused.` });
          setNewLimit('0');
        } else if (action.type === 'set-limit') {
          setActionMsg({ type: 'success', text: `Today's call limit updated to ${limitOverride}.` });
        } else {
          setActionMsg({ type: 'success', text: `Resumed ${res.resumedJobs} job(s), ${res.resumedExecutions} execution(s) restarted.` });
        }
      }
      setSelected(new Set());
      await load();
    } catch (e) {
      setActionMsg({ type: 'error', text: `${action.type === 'deny' ? 'Deny' : 'Resume'} failed: ${e.message}` });
    } finally {
      setActionLoading(false);
    }
  };

  const doSetDefault = async () => {
    const val = parseInt(defaultLimit, 10);
    if (!val || val < 1) return;
    setDefaultLimitLoading(true);
    setDefaultLimitMsg(null);
    try {
      await setDefaultQuotaLimit(val);
      setDefaultLimitMsg({ type: 'success', text: `Default call limit updated to ${val}. Takes effect from tomorrow's reset.` });
      setDefaultLimit('');
    } catch (e) {
      setDefaultLimitMsg({ type: 'error', text: 'Failed to update default call limit: ' + e.message });
    } finally {
      setDefaultLimitLoading(false);
    }
  };

  const usedPct = today.limit ? Math.min(100, Math.round((today.used / today.limit) * 100)) : 0;
  const barColor = usedPct >= 90 ? 'bg-red-500' : usedPct >= 70 ? 'bg-yellow-400' : 'bg-green-500';
  const selJobs = [...selected].map(parseJobKey);  // [{jobId, date}]
  const totalStopped = allJobKeys.length;

  const jobCallCount = (job) => job.callCount ?? job.stoppedCount ?? 0;
  const overrideAboveDailyLimit = !!(newLimit && parseInt(newLimit, 10) > (today.limit || 0));
  const hasRemainingToday = (today.remaining ?? 0) > 0;
  // Resume is allowed whenever there's spare quota today; an override above the
  // daily limit is only required once today's quota is fully used (remaining === 0).
  const canResumeJob = () => hasRemainingToday || overrideAboveDailyLimit;
  const resumeBlockedReason = () =>
    `No remaining quota today (${today.remaining ?? 0} left). Enter an override call limit greater than the daily limit (${today.limit ?? 0}) above to resume now.`;

  const selectedCallsTotal = selJobs.reduce((sum, { jobId, date }) => {
    const job = allJobs.find(j => j.jobId === jobId && j.date === date);
    return sum + (job ? jobCallCount(job) : 0);
  }, 0);
  const canResumeSelected = hasRemainingToday || overrideAboveDailyLimit;

  // Soft-warning capacity check: how many calls can actually run today once resumed.
  // If an override is typed it becomes the effective limit (see doExecute); otherwise
  // today's current limit applies. Remaining slots = effectiveLimit - usedToday.
  const overrideVal = newLimit !== '' && !isNaN(parseInt(newLimit, 10)) ? parseInt(newLimit, 10) : null;
  const effectiveLimit = overrideVal !== null ? overrideVal : (today.limit || 0);
  const effectiveRemaining = Math.max(0, effectiveLimit - (today.used || 0));
  const resumeExceedsCapacity = canResumeSelected && selectedCallsTotal > effectiveRemaining;
  const resumeWarningText = resumeExceedsCapacity
    ? `Selected jobs total ~${selectedCallsTotal} calls but only ${effectiveRemaining} slot(s) remain today — about ${selectedCallsTotal - effectiveRemaining} will re-pause immediately. Raise the override above to fit them all. (Genesys counts are upper-bound estimates, so the real overflow may be lower.)`
    : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Call Limiter</h1>
        <p className="text-sm text-gray-500 mt-1">Daily call transcription limit — manage paused executions</p>
      </div>

      {/* Today's stats row — usage bar lives inside the Used Today tile
          rather than floating between sections. */}
      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="card">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Daily Call Limit</div>
                <div className="text-2xl font-bold text-gray-900 tabular-nums mt-1">{today.limit ?? '—'}</div>
              </div>
              <span className="flex-shrink-0 w-9 h-9 bg-cloudza-50 text-cloudza-700 rounded-xl flex items-center justify-center text-sm"><FaTachometerAlt /></span>
            </div>
          </div>
          <div className="card">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Used Today</div>
                <div className="text-2xl font-bold text-gray-900 tabular-nums mt-1">{today.used ?? '—'}</div>
              </div>
              <span className="flex-shrink-0 w-9 h-9 bg-amber-50 text-amber-700 rounded-xl flex items-center justify-center text-sm"><FaPhone /></span>
            </div>
            <div className="h-1.5 rounded-full bg-gray-200 overflow-hidden mt-2.5">
              <div className={`${barColor} h-full rounded-full transition-all`} style={{ width: `${usedPct}%` }} />
            </div>
            <p className="text-xs text-gray-400 mt-1">{usedPct}% of today's limit</p>
          </div>
          <div className="card">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Remaining Today</div>
                <div className={`text-2xl font-bold tabular-nums mt-1 ${(today.remaining ?? 0) === 0 ? 'text-red-700' : 'text-gray-900'}`}>{today.remaining ?? '—'}</div>
              </div>
              <span className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-sm ${(today.remaining ?? 0) === 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                {(today.remaining ?? 0) === 0 ? <FaExclamationTriangle /> : <FaCheckCircle />}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Action bar */}
      <div className="card space-y-4">
        {/* Today's limit — one primary action; everything else stays quiet so
            the row reads as "set the limit", not five competing buttons. */}
        <div className="flex flex-wrap items-end justify-between gap-4" data-tour="quota-actions">
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1.5">
              Override today's call limit
            </label>
            <div className="flex flex-wrap gap-2 items-center">
              <input
                type="number"
                min="0"
                className="input-field !w-36"
                value={newLimit}
                onChange={e => setNewLimit(e.target.value)}
                placeholder={String(today?.limit || 500)}
              />
              <button
                disabled={!newLimit || actionLoading}
                onClick={() => {
                  const val = parseInt(newLimit, 10);
                  if (isNaN(val)) return;
                  setPendingAction({
                    type: 'set-limit',
                    jobs: [],
                    newLimit: val,
                    label: `Set today's call limit to ${val}`
                  });
                }}
                className="btn-primary"
              >
                <FaCheck />
                Apply
              </button>
              <button
                onClick={() => setPendingAction({
                  type: 'set-zero',
                  jobs: [],
                  newLimit: 0,
                  label: "Set today's call limit to 0"
                })}
                disabled={actionLoading}
                className="btn-outline !text-amber-700 !border-amber-300 hover:!bg-amber-50 font-semibold"
              >
                <FaBan />
                Set to 0
              </button>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="block text-sm font-semibold text-gray-800">Paused job actions</span>
              {selJobs.length > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-cloudza-50 text-cloudza-700 text-xs font-semibold">
                  {selJobs.length} selected
                </span>
              )}
            </div>
            <div className="flex gap-2 flex-wrap items-center">
            <button
              disabled={selJobs.length === 0 || actionLoading || !canResumeSelected}
              onClick={() => setPendingAction({
                type: 'resume',
                jobs: selJobs,
                label: `Resume ${selJobs.length} job(s)`,
                warning: resumeWarningText
              })}
              title={!canResumeSelected && selJobs.length > 0 ? resumeBlockedReason() : undefined}
              className="btn-outline"
            >
              <FaPlay />
              Resume
            </button>
            <button
              disabled={selJobs.length === 0 || actionLoading}
              onClick={() => setPendingAction({
                type: 'deny',
                jobs: selJobs,
                label: `Deny ${selJobs.length} job(s)`
              })}
              className="btn-outline !text-red-600 !border-red-300 hover:!bg-red-50 font-semibold"
            >
              <FaBan />
              Deny
            </button>
            <button
              onClick={load}
              disabled={loading}
              className="btn-outline"
            >
              <FaSyncAlt />
              Refresh
            </button>
            </div>
            {selJobs.length === 0 && (
              <p className="text-xs text-gray-400 mt-1.5">Select paused jobs in the list below to enable these.</p>
            )}
          </div>
          {actionMsg && (
            <span className={`text-sm ${actionMsg.type === 'error' ? 'text-red-600' : 'text-green-700'}`}>
              {actionMsg.text}
            </span>
          )}
          {resumeExceedsCapacity && selJobs.length > 0 && (
            <span className="inline-flex items-start gap-1.5 text-sm text-amber-700">
              <FaExclamationTriangle className="mt-0.5 flex-shrink-0 text-amber-500" />
              <span>Selected ~{selectedCallsTotal} calls vs {effectiveRemaining} slot(s) left today — ~{selectedCallsTotal - effectiveRemaining} would re-pause. Raise the override to fit them all.</span>
            </span>
          )}
        </div>

        {/* Confirmation panel */}
        {pendingAction && (
          <div className="flex flex-wrap items-center gap-3 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3">
            {pendingAction.warning && (
              <div className="w-full flex items-start gap-2 text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm">
                <FaExclamationTriangle className="mt-0.5 flex-shrink-0 text-red-500" />
                <span>{pendingAction.warning} You can still proceed — the overflow jobs stay paused and can be resumed later.</span>
              </div>
            )}
            <span className="inline-flex items-center gap-1.5 text-amber-700 font-medium text-sm">
              <FaExclamationTriangle className="flex-shrink-0 text-amber-500" />
              <span>{pendingAction.label} — type <strong>confirm</strong> to proceed</span>
            </span>
            <input
              type="text"
              autoFocus
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && confirmText.toLowerCase() === 'confirm') doExecute(pendingAction);
                if (e.key === 'Escape') { setPendingAction(null); setConfirmText(''); }
              }}
              placeholder="type confirm"
              className="input-field !w-36 !border-amber-300"
            />
            <button
              disabled={confirmText.toLowerCase() !== 'confirm'}
              onClick={() => doExecute(pendingAction)}
              className="btn-warning"
            >
              <FaCheck />
              Confirm
            </button>
            <button
              onClick={() => { setPendingAction(null); setConfirmText(''); }}
              className="btn-outline"
            >
              <FaTimes />
              Cancel
            </button>
          </div>
        )}

        {/* Default limit row */}
        <div className="border-t border-gray-100 pt-4">
          <label className="block text-sm font-semibold text-gray-800 mb-1">
            Default daily limit
          </label>
          <p className="text-xs text-gray-400 mb-2">Applies from tomorrow's midnight reset onwards — today's override above is unaffected.</p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="number"
              min="1"
              className="input-field !w-36"
              value={defaultLimit}
              onChange={e => setDefaultLimit(e.target.value)}
              placeholder="e.g. 500"
            />
            <button
              disabled={!defaultLimit || defaultLimitLoading}
              onClick={doSetDefault}
              className="btn-outline"
            >
              <><FaCheck /> {defaultLimitLoading ? "Saving…" : "Set Default"}</>
            </button>
            {defaultLimitMsg && (
              <span className={`text-sm ${defaultLimitMsg.type === 'error' ? 'text-red-600' : 'text-coral-700'}`}>
                {defaultLimitMsg.text}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Jobs table — grouped by day */}
      <div className="card !p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
            Paused Jobs
            {data && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-cloudza-50 text-cloudza-700 text-xs font-semibold">
                {totalStopped}
              </span>
            )}
          </h2>
          <span className="text-xs text-gray-400">Last 7 days</span>
        </div>

        {loading ? (
          <LoadingState variant="rows" header={false} label="Loading paused jobs" />
        ) : !data || allJobKeys.length === 0 ? (
          <div className="py-10 px-6 text-center">
            <div className="w-12 h-12 rounded-2xl bg-cloudza-50 text-cloudza-700 flex items-center justify-center mx-auto mb-3">
              <FaCheckCircle />
            </div>
            <p className="text-sm font-semibold text-gray-800">No paused jobs</p>
            <p className="text-sm text-gray-400 mt-1">Nothing has hit the call limit in the last 7 days.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-5 py-2.5 bg-[#fafafc]">
                  <input
                    type="checkbox"
                    checked={allJobKeys.length > 0 && selected.size === allJobKeys.length}
                    onChange={toggleAll}
                  />
                </th>
                <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Job ID</th>
                <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Campaign</th>
                <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Scorecard</th>
                <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Uploaded By</th>
                <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Workflows</th>
                <th className="px-5 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Last Paused</th>
                <th className="px-5 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Calls</th>
                <th className="px-5 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.days.map(day => (
                <React.Fragment key={day.date}>
                  {/* Day separator row */}
                  <tr className="bg-gray-50">
                    <td colSpan={9} className="px-5 py-2">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-semibold text-gray-600">{day.date}</span>
                        <span className="text-xs text-gray-400">
                          {day.stoppedJobCount} paused job{day.stoppedJobCount !== 1 ? 's' : ''}
                          {' · '}limit {day.limit} · used {day.used}
                        </span>
                      </div>
                    </td>
                  </tr>

                  {day.jobs.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-8 py-3 text-xs text-gray-400 italic">No paused jobs this day.</td>
                    </tr>
                  ) : (
                    <>
                      {day.jobs.map(job => (
                        <React.Fragment key={job.jobId}>
                          <tr
                            className={`hover:bg-cloudza-50/40 transition-colors cursor-pointer ${selected.has(jobKey(day.date, job.jobId)) ? 'bg-cloudza-50' : ''}`}
                            onClick={() => toggleSelect(day.date, job.jobId)}
                          >
                            <td className="px-5 py-3" onClick={e => e.stopPropagation()}>
                              <input type="checkbox" checked={selected.has(jobKey(day.date, job.jobId))} onChange={() => toggleSelect(day.date, job.jobId)} />
                            </td>
                            <td className="px-5 py-3 font-mono text-xs text-gray-700 max-w-[180px]" onClick={e => e.stopPropagation()}>
                              <div className="flex items-center gap-1 min-w-0">
                                <Link
                                  to={`/jobs/${job.jobId}`}
                                  className="text-cloudza-600 hover:underline truncate"
                                  title="View job detail"
                                >
                                  {job.jobId}
                                </Link>
                                <button
                                  className="flex-shrink-0 ml-1 p-1.5 rounded-lg text-gray-400 hover:text-cloudza-600 hover:bg-cloudza-50 transition-colors"
                                  onClick={() => expandJob(day.date, job.jobId)}
                                  title={expandedJob === jobKey(day.date, job.jobId) ? 'Hide calls' : 'View paused calls'}
                                  aria-label={expandedJob === jobKey(day.date, job.jobId) ? 'Hide paused calls' : 'View paused calls'}
                                >
                                  {expandedJob === jobKey(day.date, job.jobId) ? <FaChevronUp /> : <FaChevronDown />}
                                </button>
                              </div>
                            </td>
                            <td className="px-5 py-3 text-gray-700">{job.campaign || '—'}</td>
                            <td className="px-5 py-3 text-gray-700">{job.scorecard || '—'}</td>
                            <td className="px-5 py-3 text-gray-500">{job.uploadedBy || '—'}</td>
                            <td className="px-5 py-3">
                              <WorkflowBadges types={job.workflowTypes} />
                            </td>
                            <td className="px-5 py-3 text-right text-gray-500 whitespace-nowrap">
                              {fmt(job.lastStoppedAt)}
                            </td>
                            <td className="px-5 py-3 text-right">
                              <CallCount job={job} />
                            </td>
                            <td className="px-5 py-3 text-right" onClick={e => e.stopPropagation()}>
                              <div className="flex flex-wrap justify-end gap-2">
                                <div className="relative group">
                                  <button
                                    onClick={() => setPendingAction({
                                      type: 'resume',
                                      jobs: [{ jobId: job.jobId, date: day.date }],
                                      label: `Resume job ${job.jobId}`
                                    })}
                                    disabled={actionLoading || !canResumeJob(job)}
                                    className="px-2.5 py-1 text-xs font-semibold bg-green-50 text-green-700 border border-green-200 rounded-full hover:bg-green-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    <FaPlay className="inline mr-1" />
                                    Resume
                                  </button>
                                  <div className="absolute bottom-full right-0 mb-2 hidden group-hover:flex items-start gap-1.5 bg-amber-50 border border-amber-300 text-amber-800 text-xs rounded-lg px-3 py-2 shadow-lg w-64 z-50 pointer-events-none">
                                    <FaExclamationTriangle className="mt-0.5 flex-shrink-0 text-amber-500" />
                                    <span className="font-medium leading-snug">
                                      {canResumeJob(job)
                                        ? 'Make sure that you have increased the daily call limit before resuming'
                                        : resumeBlockedReason(job)}
                                    </span>
                                  </div>
                                </div>
                                <button
                                  onClick={() => setPendingAction({
                                    type: 'deny',
                                    jobs: [{ jobId: job.jobId, date: day.date }],
                                    label: `Deny job ${job.jobId}`
                                  })}
                                  disabled={actionLoading}
                                  className="px-2.5 py-1 text-xs font-semibold bg-red-50 text-red-700 border border-red-200 rounded-full hover:bg-red-100 disabled:opacity-40"
                                >
                                  <FaBan className="inline mr-1" />
                                  Deny
                                </button>
                              </div>
                            </td>
                          </tr>

                          {/* Expanded calls row */}
                          {expandedJob === jobKey(day.date, job.jobId) && (
                            <tr>
                              <td colSpan={9} className="bg-gray-50 px-8 py-4">
                                {expandLoading ? (
                                  <LoadingState variant="rows" header={false} label="Loading paused calls" />
                                ) : expandedExecs.length === 0 ? (
                                  <p className="text-sm text-gray-400">No paused call records found.</p>
                                ) : (
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="border-b border-gray-200">
                                        <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Workflow</th>
                                        <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Paused At</th>
                                        <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Call ID</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                      {expandedExecs.map(ex => (
                                        <tr key={ex.sk}>
                                          <td className="px-3 py-2">
                                            <WorkflowBadges types={[ex.workflowType]} />
                                          </td>
                                          <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{fmt(ex.stoppedAt)}</td>
                                          <td className="px-3 py-2 font-mono text-gray-500 truncate max-w-[400px]">{ex.callId || '—'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}

                      {/* Per-day Load More button */}
                      {dayNextKeys[day.date] && (
                        <tr>
                          <td colSpan={9} className="px-5 py-3 text-center border-t border-gray-100">
                            <button
                              onClick={() => loadMoreForDay(day.date)}
                              disabled={dayLoadingMore[day.date]}
                              className="px-4 py-1.5 text-xs font-semibold bg-gray-100 text-gray-600 rounded-full border border-gray-200 hover:bg-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              {dayLoadingMore[day.date] ? 'Loading…' : <><FaChevronDown className="inline mr-1" /> Load more for {day.date}</>}
                            </button>
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
